import axios from 'axios';
import { isEmpty } from 'lodash';
import mapper, { reverseMapper } from './idMapper';

const axiosInstance = axios.create({
    baseURL: 'https://vrbank-in-holstein.meinbankfinder.de/api',
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    },
});

axiosInstance.interceptors.request.use(
    (config) => {
        if (
            JSON.parse(window.sessionStorage.hausbankfinder).consultantMode &&
            !config.url.includes('getCustomerProducts') &&
            !config.url.includes('customer-external-recommendations') &&
            !config.url.includes('customer-probabilities') &&
            !config.url.includes('campaign/advisorType')
        ) {
            throw new axios.Cancel(
                `${config.method} ${config.baseURL}${config.url} - canceled by the consultant mode.`
            );
        } else {
            return config;
        }
    },
    function(error) {
        // Do something with request error
        return Promise.reject(error);
    }
);

function getCustomerCode() {
    if (isEmpty(window.sessionStorage) || isEmpty(window.sessionStorage.hausbankfinder)) {
        return null;
    }

    return JSON.parse(window.sessionStorage.hausbankfinder).code;
}

function orderRecommendations(a, b) {
    if (a.recommendation < b.recommendation) {
        return -1;
    }
    if (a.recommendation > b.recommendation) {
        return 1;
    }
    return 0;
}

export default {
    getCloudData(code) {
        return new Promise((resolve, reject) => {
            axiosInstance
                .post(`/finder/customers/v2/getCustomerProducts/${code}/pk`)
                .then((response) => {
                    let cleanedAppResponse = {};
                    let cleanedPersonalData = {};

                    const allProducts = [];
                    const bank = [];
                    const interesse = [];
                    const irrelevant = [];
                    const fremd = [];
                    const checkstrecke = [];
                    const versorgt = [];

                    response.data.response.productCategories.forEach((category) => {
                        category.productDTOList.forEach((product) => {
                            if (product.code === 'mitgliedschaft_mitgliedschaft_mitgliedschaft') {
                                cleanedPersonalData['member'] = product.status === '1';
                            } else {
                                allProducts.push(product.code);
                                if (product.status === '1') {
                                    bank.push(product.code);
                                }
                                if (product.statusDetails && product.statusDetails.length > 0) {
                                    // detailExample: { "code": "Irrelevant", "value": "1" }
                                    product.statusDetails.forEach((detail) => {
                                        if (
                                            detail.hasOwnProperty('code') &&
                                            detail.hasOwnProperty('value') &&
                                            detail.code !== null &&
                                            detail.value !== null &&
                                            typeof detail.code === 'string' &&
                                            detail.value.toString() === '1'
                                        ) {
                                            if (detail.code.toLowerCase() === 'interesse') {
                                                interesse.push(product.code);
                                            }
                                            if (detail.code.toLowerCase() === 'fremd') {
                                                fremd.push(product.code);
                                            }
                                            if (detail.code.toLowerCase() === 'irrelevant') {
                                                irrelevant.push(product.code);
                                            }
                                            if (detail.code.toLowerCase() === 'checkstrecke') {
                                                checkstrecke.push(product.code);
                                            }
                                            if (detail.code.toLowerCase() === 'versorgt') {
                                                versorgt.push(product.code);
                                            }
                                        }
                                    });
                                }
                            }
                        });
                    });

                    // be sure all ids have a 0 state!
                    allProducts.forEach((productId) => {
                        const mappedId = mapper(productId);
                        cleanedAppResponse[mappedId] = 0;
                        cleanedAppResponse[`Interesse_${mappedId}`] = 0;
                        cleanedAppResponse[`Fremd_${mappedId}`] = 0;
                        cleanedAppResponse[`Irrelevant_${mappedId}`] = 0;
                        cleanedAppResponse[`CheckStrecke_${mappedId}`] = 0;
                        cleanedAppResponse[`Versorgt_${mappedId}`] = 0;
                    });

                    bank.forEach((productId) => {
                        const mappedId = mapper(productId);
                        cleanedAppResponse[mappedId] = 1;
                    });
                    interesse.forEach((productId) => {
                        const mappedId = mapper(productId);
                        cleanedAppResponse[`Interesse_${mappedId}`] = 1;
                    });
                    fremd.forEach((productId) => {
                        const mappedId = mapper(productId);
                        cleanedAppResponse[`Fremd_${mappedId}`] = 1;
                    });
                    irrelevant.forEach((productId) => {
                        const mappedId = mapper(productId);
                        cleanedAppResponse[`Irrelevant_${mappedId}`] = 1;
                    });
                    checkstrecke.forEach((productId) => {
                        const mappedId = mapper(productId);
                        cleanedAppResponse[`CheckStrecke_${mappedId}`] = 1;
                    });
                    versorgt.forEach((productId) => {
                        const mappedId = mapper(productId);
                        cleanedAppResponse[`Versorgt_${mappedId}`] = 1;
                    });

                    // personal data
                    cleanedPersonalData['age'] = parseInt(response.data.response.age) || 0;
                    cleanedPersonalData['retired'] =
                        response.data.response.pension.toLowerCase().includes('ja') || false;
                    cleanedPersonalData['sharedClient'] =
                        response.data.response.sharedClient.toLowerCase().includes('ja') || false;
                    cleanedPersonalData['starsCount'] = parseInt(response.data.response.starsCount) || null;

                    // date
                    let date = null;
                    try {
                        date = response.data.response.snapshotDate;
                    } catch (e) {
                        // just do nothing, not parsable, date will be "unbekannt"
                    }

                    return resolve({
                        updateDate: date,
                        personalData: cleanedPersonalData,
                        products: cleanedAppResponse,
                    });
                })
                .catch((error) => {
                    console.error(error);
                    return reject(error);
                });
        });
    },

    getRecommendations() {
        return new Promise((resolve, reject) => {
            let recommendations = [];
            let mappedRecommendations = [];
            axiosInstance
                .post(`/customer-external-recommendations/${getCustomerCode()}/pk`)
                .then((response) => {
                    response.data.response.forEach((module) => {
                        module.categoryDTOList.forEach((category) => {
                            category.productDTOList.forEach((product) => {
                                recommendations.push(product);
                            });
                        });
                    });
                    // ---
                    recommendations.sort(orderRecommendations);
                    recommendations.forEach((product) => {
                        const code = mapper(product.code);
                        if (code !== null) {
                            mappedRecommendations.push(code);
                        }
                    });
                    return resolve(mappedRecommendations);
                })
                .catch((e) => {
                    console.error(e);
                    return reject(e);
                });
        });
    },

    getProbabilities() {
        return new Promise((resolve, reject) => {
            let recommendations = [];
            axiosInstance
                .post(`/customer-probabilities/${getCustomerCode()}/pk`)
                .then((response) => {
                    response.data.response.forEach((module) => {
                        module.categoryProbabilityDTOList.forEach((category) => {
                            recommendations.push(category);
                        });
                    });
                    // ---
                    return resolve(recommendations);
                })
                .catch((e) => {
                    console.error(e);
                    return reject(e);
                });
        });
    },

    updateProductOptionInCloud(productId, option, payload) {
        const availableOptions = {
            // BANK: '', // cant be set in prefilled mode!
            INTEREST: 'Interesse', // <-- user
            EXTERNAL: 'Fremd', // <-- user
            HIDE: 'Irrelevant', // <-- app
            RECOMMEND: 'Checkstrecke', // <-- app
        };

        if (!Object.keys(availableOptions).includes(option)) {
            throw new Error(`Unknown option in updateProductOptionInCloud: "${option.toString()}"`);
        }

        const code = reverseMapper(productId);
        if (code === null) {
            return;
        }
        return axiosInstance.post('/finder/customers/v2/saveCustomerProducts', {
            customerCode: `${getCustomerCode()}`,
            adviserType: 'PK',
            customerProductStatusList: [
                {
                    code: code,
                    statusDetails: [
                        {
                            code: `${availableOptions[option]}`,
                            value: `${payload ? '1' : '0'}`,
                        },
                    ],
                },
            ],
        });
    },

    updateKeyInCloud(key, payload) {
        let jsonBody = {};

        if (key === 'Schon_Im_Ruhestand') {
            jsonBody = {
                pension: payload ? 'Ja' : 'Nein',
            };
        } else if (key === 'Alter') {
            jsonBody = {
                age: parseInt(payload),
            };
        } else {
            throw new Error('Invalid key given!');
        }

        return axiosInstance.post('/finder/customers/saveCustomerData', {
            customerCode: getCustomerCode(),
            adviserType: 'PK',
            ...jsonBody,
        });
    },

    setProductCheckstrecke(products) {
        if (products.length === 0) {
            return;
        }

        let jsonBody = {
            customerCode: getCustomerCode(),
            adviserType: 'PK',
            customerProductStatusList: [],
        };

        for (const [key, value] of Object.entries(products)) {
            const code = reverseMapper(key);
            if (code !== null) {
                jsonBody.customerProductStatusList.push({
                    code: code,
                    statusDetails: [
                        {
                            code: 'Checkstrecke',
                            value: value.toString(),
                        },
                    ],
                });
            }
        }

        return axiosInstance.post('/finder/customers/v2/saveCustomerProducts', jsonBody);
    },

    /**
     * @param {String[]} products
     * @return
     */
    resetProductInterest(products) {
        if (products.length === 0) {
            return;
        }

        let jsonBody = {
            customerCode: getCustomerCode(),
            adviserType: 'PK',
            customerProductStatusList: [],
        };

        for (let product of products) {
            const code = reverseMapper(product);
            if (code !== null) {
                jsonBody.customerProductStatusList.push({
                    code: code,
                    statusDetails: [
                        {
                            code: 'Interesse',
                            value: '0',
                        },
                    ],
                });
            }
        }

        return axiosInstance.post('/finder/customers/v2/saveCustomerProducts', jsonBody);
    },

    resetProducts(products) {
        return new Promise((resolve, reject) => {
            if (products.length === 0) {
                return resolve();
            }

            let jsonBody = {
                customerCode: getCustomerCode(),
                adviserType: 'PK',
                customerProductStatusList: [],
            };

            for (let product of products) {
                const code = reverseMapper(product.id);
                if (code !== null) {
                    jsonBody.customerProductStatusList.push({
                        code: code,
                        statusDetails: [
                            {
                                code: 'Interesse',
                                value: '0',
                            },
                            {
                                code: 'Irrelevant',
                                value: '0',
                            },
                            {
                                code: 'Checkstrecke',
                                value: '0',
                            },
                            {
                                code: 'Fremd',
                                value: '0',
                            },
                        ],
                    });
                }
            }

            axiosInstance
                .post('/finder/customers/v2/saveCustomerProducts', jsonBody)
                .then((response) => {
                    return resolve(response);
                })
                .catch((response) => {
                    return reject(response);
                });
        });
    },

    /**
     * @param {String} extraProduct
     * @param {Boolean} status
     * @return
     */
    setExtraProducts(extraProduct, status) {
        const validProducts = ['AR_4_Vorsorge_AusreichendVermoegen', 'AR_5_Immobilie_AbgezahlteImmobilie'];

        if (!validProducts.includes(extraProduct)) {
            throw new Error('Invalid product ID');
        }

        return axiosInstance.post('/finder/customers/v2/saveCustomerProducts', {
            customerCode: getCustomerCode(),
            adviserType: 'PK',
            customerProductStatusList: [
                {
                    code: reverseMapper(extraProduct),
                    statusDetails: [
                        {
                            code: 'Versorgt',
                            value: status ? '1' : '0',
                        },
                    ],
                },
            ],
        });
    },

    /**
     * @return {Promise<AxiosResponse<any>>}
     */
    getCampaign() {
        return axiosInstance.post(`/finder/customers/${getCustomerCode()}/campaign/advisorType/pk`);
    },

    sendContactForm(data) {
        const mailBody = {
            customerInfo: {
                alreadyBankClient: getCustomerCode() !== null,
                emailAddress: data.contactData.email,
                firstName: data.contactData.firstname,
                lastName: data.contactData.lastname,
                phoneNumber: data.contactData.phone,
                details: {
                    Termin: data.contactData.appointment ? 'Ja' : 'Nein',
                    Kundennummer: data.contactData.customerId,
                },
            },
            finderInfo: {
                Interesse: data.clipboardData.interest.join(', '),
                Fremd: data.clipboardData.external.join(', '),
                Fehlt: data.clipboardData.missing.join(', '),
                Verstecken: data.clipboardData.hidden.join(', '),
                Sonstiges: data.clipboardData.other.join(', '),
            },
            moreInfo: {
                Mitgliedschaft: data.contactData.member ? 'Ja' : 'Nein',
            },
            subject: `Hausbankfinder Kontaktformular - ${data.contactData.firstname} ${data.contactData.lastname}`,
            customerCode: getCustomerCode() === null ? '-' : `${getCustomerCode()}`,
        };

        return axiosInstance.post('/finder/contactbank', mailBody);
    },
};
